import { useEffect, useState } from 'react';
import { OrderInvoiceDTO } from '@lambda/apis/src/order/types';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { logger } from '@/lib/logger';

type UseOrderInvoicesResult = {
  invoices: OrderInvoiceDTO[] | null;
  isFetching: boolean;
  error: Error | null;
  hasError: boolean;
};

export default function useOrderInvoices(
  orderId: string | undefined,
): UseOrderInvoicesResult {
  const { customer } = useCustomerContext();
  const [invoices, setInvoices] = useState<OrderInvoiceDTO[] | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isActive = true;

    if (!orderId || !customer?.email) {
      setInvoices(null);
      setIsFetching(false);
      setError(null);

      return () => {
        isActive = false;
      };
    }

    const fetchOrderInvoices = async () => {
      setIsFetching(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/accounts/orders/invoices/${encodeURIComponent(
            orderId,
          )}?email=${encodeURIComponent(customer.email)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache',
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = (await response.json()) as OrderInvoiceDTO[] | null;

        if (!isActive) {
          return;
        }

        setInvoices(data);
      } catch (err) {
        if (!isActive) {
          return;
        }

        const fetchError =
          err instanceof Error ? err : new Error('Failed to fetch invoices');

        setInvoices(null);
        setError(fetchError);
        logger.error(
          { err: fetchError, orderId },
          'Error fetching order invoices',
        );
      } finally {
        if (isActive) {
          setIsFetching(false);
        }
      }
    };

    fetchOrderInvoices();

    return () => {
      isActive = false;
    };
  }, [customer?.email, orderId]);

  return {
    invoices,
    isFetching,
    error,
    hasError: error !== null,
  };
}
